import { Action, action, computed, Computed } from 'easy-peasy';
import type {
  EditorMode,
  NodeKind,
  OpenModal,
  ValidationIssue,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
  WorkflowGraphSettings,
  SnippetSpec,
} from '../types';
import {
  addEdge as addEdgeOp,
  addNode as addNodeOp,
  dropEdgesOfVanishedPorts,
  insertNodeFromPort,
  makeNode,
  portLinkWouldCycle,
  removeEdge as removeEdgeOp,
  removeNode as removeNodeOp,
  updateNode as updateNodeOp,
} from '../utils/graph-ops';
import { edgeStyleForPort } from '../utils/catalog';
import type {
  NodeHints,
  Workflow,
  WorkflowCatalog,
  WorkflowHints,
  WorkflowVersion,
} from '../api/types';
import type { ValidationStatus } from '../hooks/useGraphValidation';

export interface LoadPayload {
  workflow: Workflow;
  graph: WorkflowGraph;
  /** Id do rascunho, se o que está sendo editado já é um. */
  versionId: string | null;
  targetSellerIds: string[];
  publishedVersion: WorkflowVersion | null;
}
import {
  expandReenvio,
  expandSpecSnippet,
  rebuildSnippet,
  syncSnippetFromTrack,
  type SpecSnippetKind,
} from '../utils/snippet-expand';
import { nodesRoomLeft } from '../utils/graph-limits';

/**
 * Um passo do undo/redo: tudo que é conteúdo da versão e o usuário edita no
 * canvas. Filiais-alvo entram porque são da versão (vão no mesmo PUT).
 */
export interface HistoryEntry {
  graph: WorkflowGraph;
  targetSellerIds: string[];
}

/**
 * Menu rápido "o que vem depois desta porta?". Abre ao soltar uma ligação
 * no vazio ou ao clicar numa porta de saída.
 * `screen` ancora o popover (coordenadas da tela); `flow`, quando vem,
 * é onde o passo novo nasce no canvas — sem ele, nasce ao lado de `screen`.
 */
export interface QuickAddState {
  screen: { x: number; y: number };
  fromNodeId: string;
  fromPortId: string;
  flow?: { x: number; y: number };
}

export type SidebarTab = 'step' | 'issues';

export interface FocusedIssue {
  /** `issueKey` da pendência (ver `utils/issue-order`). */
  key: string;
  nodeId: string | null;
  field: string | null;
  seq: number;
}

export interface WorkflowEditorModel {
  /** Metadados (nome, operação, liga-desliga). Mudam via PATCH, fora do undo. */
  workflow: Workflow | null;
  /** Conteúdo da versão em edição. É o que vai no PUT de rascunho. */
  graph: WorkflowGraph | null;
  /** Id do rascunho no back. `null` = o próximo salvar cria um. */
  versionId: string | null;
  targetSellerIds: string[];
  /** Versão no ar, para "Descartar" voltar a ela. */
  publishedVersion: WorkflowVersion | null;
  /**
   * Resultado da última validação (`hooks/useGraphValidation`, que chama
   * o `/validate` do backend). Lido pelo canvas (destaque do nó) e pelo
   * painel (lista de pendências).
   */
  validationIssues: ValidationIssue[];
  /**
   * Dicas por passo da mesma validação (o que vem antes de cada um). Os
   * formulários leem daqui o que mostrar. Fica a última resposta enquanto
   * a próxima não chega.
   */
  validationHints: WorkflowHints;
  /**
   * `validating`: o grafo mudou e a resposta ainda não chegou (a lista é a
   * da versão anterior). `error`: o `/validate` falhou.
   */
  validationStatus: ValidationStatus;
  selectedNodeId: string | null;
  selectedSnippetId: string | null;
  /**
   * Aba da lateral: o passo selecionado ou a lista de pendências. Com
   * `'issues'` a lateral fica aberta mesmo sem passo selecionado.
   */
  sidebarTab: SidebarTab;
  /**
   * Pendência em foco: o painel destaca o campo dela. Muda a cada clique
   * (`seq`), mesmo que seja a mesma pendência, pra repetir o destaque.
   */
  focusedIssue: FocusedIssue | null;
  /**
   * O usuário chegou ao passo pela lista ou pelo "‹ ›": ao zerar as
   * pendências do passo, o painel avança sozinho pra próxima. Clicar num
   * passo no canvas desliga.
   */
  reviewingIssues: boolean;
  mode: EditorMode;
  /** Menu rápido aberto, ou `null`. Efêmero: fora do undo e do dirty. */
  quickAdd: QuickAddState | null;
  openModal: OpenModal;
  /**
   * Por que o seletor de filiais (`openModal === 'targets'`) está aberto.
   * `'publish'` = Publicar com 0 filiais: o modal troca "Aplicar" por
   * "Continuar para publicar" e, ao confirmar, a página segue pra
   * confirmação de publicação. `null` = abertura normal pelo canvas.
   * `'view'` = aberto pelo selo "Vale para" de um Envio: só leitura, com
   * "Editar filiais" pra trocar pro modo normal. Qualquer `setOpenModal`
   * zera a intenção.
   */
  targetsModalIntent: 'publish' | 'view' | null;
  /**
   * Nó que abriu o seletor em `'view'`: o selo "Prazo ausente" considera só
   * os métodos desse Envio, não o grafo inteiro. Zera com a intenção.
   */
  targetsFocusNodeId: string | null;
  /**
   * Passo em foco no seletor de próximo passo do painel: o canvas destaca
   * o card para a pessoa ver qual é antes de escolher. Só visual, não entra
   * no histórico.
   */
  stepPreviewId: string | null;
  /**
   * Contador que o canvas observa para organizar o fluxo (ELK) e enquadrar,
   * depois de uma carga feita fora dele (restaurar a publicada, trazer
   * versão do histórico). Só o valor mudar importa.
   */
  autoLayoutSeq: number;
  /**
   * Contador que o canvas observa pra chamar `fitView` depois de uma carga
   * feita fora dele (restaurar a publicada, trazer versão do histórico).
   * Só o valor mudar importa; nunca é lido como número.
   */
  fitViewSeq: number;
  /**
   * Passo pedido de fora do canvas (o `?node=` do "Abrir workflow" do painel
   * de Pedidos): o canvas centraliza nele quando `focusNodeSeq` muda, depois
   * do auto-layout da carga. `load` zera o id; o contador só sobe.
   */
  focusedNodeId: string | null;
  focusNodeSeq: number;
  /**
   * `?node=` pedido numa carga do zero: fica aqui até o canvas ter o React
   * Flow pronto, os nós medidos e a organização da carga feita
   * (`consumeNodeFocus`). Antes disso a seleção seria apagada pela primeira
   * `onSelectionChange` e o enquadramento rodaria sem medidas. `load` zera.
   */
  pendingFocusNodeId: string | null;
  /**
   * Sobe a cada ligação recusada por fazer o fluxo voltar (ciclo). A página
   * observa e mostra o aviso. Mesmo esquema do `fitViewSeq`.
   */
  cycleRefusedSeq: number;
  dirty: boolean;
  /** Stack de estados anteriores pro undo. Limitado a HISTORY_LIMIT. */
  past: HistoryEntry[];
  /** Stack de estados desfeitos pra redo. Limpa em qualquer nova edição. */
  future: HistoryEntry[];

  selectedNode: Computed<WorkflowEditorModel, WorkflowNode | null>;
  selectedSnippet: Computed<
    WorkflowEditorModel,
    import('../types').WorkflowSnippet | null
  >;

  // lifecycle
  load: Action<WorkflowEditorModel, LoadPayload>;
  /** Metadados após PATCH. Sem undo, sem dirty. */
  setWorkflow: Action<WorkflowEditorModel, Workflow>;
  /** Após PUT de rascunho com sucesso: fixa o id e zera dirty. */
  setSavedVersion: Action<WorkflowEditorModel, WorkflowVersion>;
  /**
   * A versão em edição virou a publicada. `targetSellerIds`, quando vem, é o
   * que foi publicado de fato (filiais mantidas em outro workflow saíram):
   * o canvas passa a mostrar isso, sem entrar no desfazer. O histórico de
   * desfazer zera: publicar é ponto de corte.
   */
  markPublished: Action<
    WorkflowEditorModel,
    { version: WorkflowVersion; workflow: Workflow; targetSellerIds?: string[] }
  >;
  /** Filiais-alvo são da versão: entram no undo e marcam dirty. */
  setTargetSellerIds: Action<WorkflowEditorModel, string[]>;
  /** Resultado da validação. Não muda o estado se os issues forem iguais. */
  setValidationIssues: Action<WorkflowEditorModel, ValidationIssue[]>;
  setValidationHints: Action<WorkflowEditorModel, WorkflowHints>;
  setValidationStatus: Action<WorkflowEditorModel, ValidationStatus>;
  /** Nome local após renomear. Sem undo, sem dirty — é do workflow, não da versão. */
  setName: Action<WorkflowEditorModel, string>;
  markPristine: Action<WorkflowEditorModel>;
  /**
   * Conteúdo recuperado do backup do navegador (`hooks/useDraftBackup`).
   * Marca dirty porque o servidor ainda não tem isso; zera o histórico
   * porque o "antes" do backup é o que acabou de ser carregado.
   */
  restoreFromBackup: Action<
    WorkflowEditorModel,
    { graph: WorkflowGraph; targetSellerIds: string[] }
  >;
  /**
   * "Carregar versão" do histórico: substitui grafo + filiais pelo conteúdo
   * de uma versão publicada. É uma edição como outra qualquer — entra no
   * undo (dá pra voltar) e marca dirty; o rascunho só muda ao salvar.
   */
  loadFromVersion: Action<
    WorkflowEditorModel,
    { graph: WorkflowGraph; targetSellerIds: string[] }
  >;

  // history
  undo: Action<WorkflowEditorModel>;
  redo: Action<WorkflowEditorModel>;

  // selection / panel
  /**
   * Seleção feita pelo usuário (canvas, Esc, fechar). Com passo, a lateral
   * vai pra aba do passo e o avanço automático desliga.
   */
  selectNode: Action<WorkflowEditorModel, string | null>;
  selectSnippet: Action<WorkflowEditorModel, string | null>;
  setSidebarTab: Action<WorkflowEditorModel, SidebarTab>;
  /**
   * Leva à pendência: seleciona o passo dela, abre a aba do passo e pede
   * o destaque do campo. Pendência sem passo só fica em foco na lista.
   */
  focusIssue: Action<
    WorkflowEditorModel,
    { key: string; nodeId?: string; field?: string }
  >;
  setOpenModal: Action<WorkflowEditorModel, OpenModal>;
  /** Abre o seletor de filiais em modo publicação (ver `targetsModalIntent`). */
  openTargetsForPublish: Action<WorkflowEditorModel>;
  /** Abre o seletor só pra ver as filiais, do ponto de vista deste Envio. */
  openTargetsReadOnly: Action<WorkflowEditorModel, string>;
  /**
   * Seleciona o passo e pede ao canvas pra centralizar nele (ver
   * `focusNodeSeq`). Diferente do `focusIssue`, não liga a revisão de
   * pendências.
   */
  focusNode: Action<WorkflowEditorModel, string>;
  /** Guarda o passo do `?node=` até o canvas estar pronto (ver `pendingFocusNodeId`). */
  requestNodeFocus: Action<WorkflowEditorModel, string>;
  /** O canvas ficou pronto: aplica o `focusNode` pendente, se houver. */
  consumeNodeFocus: Action<WorkflowEditorModel>;
  /** Pede ao canvas um `fitView` no próximo frame (ver `fitViewSeq`). */
  requestFitView: Action<WorkflowEditorModel>;
  setStepPreview: Action<WorkflowEditorModel, string | null>;
  requestAutoLayout: Action<WorkflowEditorModel>;
  /**
   * Avisa que uma ligação foi recusada por ciclo (ver `cycleRefusedSeq`).
   * Para quem confere antes de mexer no grafo (reconectar no canvas).
   */
  refuseCycle: Action<WorkflowEditorModel>;

  // node ops
  /** O catálogo dá o config inicial do passo. */
  addNode: Action<
    WorkflowEditorModel,
    {
      kind: NodeKind;
      position?: { x: number; y: number };
      catalog: WorkflowCatalog;
    }
  >;
  updateNodePatch: Action<
    WorkflowEditorModel,
    { nodeId: string; patch: Partial<WorkflowNode> }
  >;
  /**
   * Com `catalog`, a mesma entrada de undo também tira as ligações das
   * portas que o patch fez sumir (`dropEdgesOfVanishedPorts`: regra do
   * Acompanhar entrega apagada). Sem ele, só mexe no config.
   */
  updateNodeConfigPatch: Action<
    WorkflowEditorModel,
    {
      nodeId: string;
      configPatch: Record<string, unknown>;
      catalog?: WorkflowCatalog;
    }
  >;
  removeNode: Action<WorkflowEditorModel, string>;
  removeSnippet: Action<WorkflowEditorModel, string>;
  /** Ajustes da versão que moram no grafo (limite de envios por pedido). */
  setGraphSettings: Action<WorkflowEditorModel, WorkflowGraphSettings>;
  /**
   * Cria um passo já ligado à porta de origem e o seleciona, numa única
   * entrada de undo. Se a porta já levava a outro passo, o novo entra no
   * meio (ver `insertNodeFromPort`).
   */
  addNodeFromPort: Action<
    WorkflowEditorModel,
    {
      kind: NodeKind;
      position: { x: number; y: number };
      fromNodeId: string;
      fromPortId: string;
      catalog: WorkflowCatalog;
    }
  >;
  /**
   * Remove um caso da Condição E as arestas que saíam daquela porta, numa
   * entrada de undo — senão sobra uma ligação pendurada numa saída que não
   * existe mais.
   */
  removeSwitchCase: Action<
    WorkflowEditorModel,
    { nodeId: string; caseId: string }
  >;
  /**
   * Grava posições de nós: drop de arrasto (um ou vários, inclusive o grupo
   * inteiro), setas do teclado e auto-layout. Uma chamada = uma entrada de
   * undo. Não marca dirty. Ignora a chamada se nenhuma posição mudou.
   */
  applyAutoLayoutPositions: Action<
    WorkflowEditorModel,
    Record<string, { x: number; y: number }>
  >;

  // edges
  addEdge: Action<
    WorkflowEditorModel,
    {
      fromNodeId: string;
      fromPortId: string;
      toNodeId: string;
      style?: WorkflowEdge['style'];
    }
  >;
  removeEdge: Action<WorkflowEditorModel, string>;

  // connect mode
  startConnect: Action<
    WorkflowEditorModel,
    {
      sourceNodeId: string;
      sourcePortId: string;
      sourceStyle: WorkflowEdge['style'];
    }
  >;
  cancelConnect: Action<WorkflowEditorModel>;
  completeConnect: Action<WorkflowEditorModel, { toNodeId: string }>;

  // menu rápido
  openQuickAdd: Action<WorkflowEditorModel, QuickAddState>;
  closeQuickAdd: Action<WorkflowEditorModel>;

  // snippets
  addSnippet: Action<
    WorkflowEditorModel,
    {
      /**
       * Receita da paleta (`envio-inatividade` vira um grupo `envio` com as
       * duas regras) ou o Reenvio antigo.
       */
      kind: SpecSnippetKind | 'reenvio';
      attachToNodeId?: string;
      attachToPortId?: string;
      position?: { x: number; y: number };
      /** Config dos passos e o passo de envio do perfil. */
      catalog: WorkflowCatalog;
    }
  >;
  /**
   * Troca a receita de um grupo novo e remonta os passos dele. Sem espaço
   * no fluxo para os passos a mais, não muda nada.
   */
  updateSnippetSpec: Action<
    WorkflowEditorModel,
    { snippetId: string; spec: SnippetSpec; catalog: WorkflowCatalog }
  >;
}

const HISTORY_LIMIT = 50;

const setDirty = <T extends { dirty: boolean }>(state: T) => {
  state.dirty = true;
};

/**
 * Snapshot do conteúdo atual (graph + filiais) no stack de undo. Limpa o
 * stack de redo (qualquer edição depois de um undo descarta a "linha do
 * tempo alternativa"). Chamado no início de toda action que muta o
 * conteúdo da versão.
 */
function pushHistory(state: {
  graph: WorkflowGraph | null;
  targetSellerIds: string[];
  past: HistoryEntry[];
  future: HistoryEntry[];
}) {
  if (!state.graph) return;
  const next = [
    ...state.past,
    { graph: state.graph, targetSellerIds: state.targetSellerIds },
  ];
  if (next.length > HISTORY_LIMIT) next.shift();
  state.past = next;
  state.future = [];
}

/** Aplica um snapshot do histórico e limpa seleção que não existe mais nele. */
function applyHistoryEntry(
  state: {
    graph: WorkflowGraph | null;
    targetSellerIds: string[];
    selectedNodeId: string | null;
    selectedSnippetId: string | null;
  },
  entry: HistoryEntry,
) {
  state.graph = entry.graph;
  state.targetSellerIds = entry.targetSellerIds;
  if (
    state.selectedNodeId &&
    !entry.graph.nodes.some((n) => n.id === state.selectedNodeId)
  ) {
    state.selectedNodeId = null;
  }
  if (
    state.selectedSnippetId &&
    !entry.graph.snippets.some((s) => s.id === state.selectedSnippetId)
  ) {
    state.selectedSnippetId = null;
  }
}

const sameIssues = (a: ValidationIssue[], b: ValidationIssue[]) =>
  a.length === b.length &&
  a.every(
    (x, i) =>
      x.nodeId === b[i].nodeId &&
      x.edgeId === b[i].edgeId &&
      x.severity === b[i].severity &&
      x.code === b[i].code &&
      x.field === b[i].field &&
      x.detail === b[i].detail &&
      x.message === b[i].message,
  );

const sameNodeHints = (a: NodeHints, b: NodeHints | undefined) =>
  Boolean(b) &&
  a.upstreamDispatch === b?.upstreamDispatch &&
  a.upstreamDispatchNodeId === b?.upstreamDispatchNodeId &&
  a.upstreamCarrier === b?.upstreamCarrier &&
  a.upstreamPromisedDate === b?.upstreamPromisedDate &&
  a.needsCancelBefore === b?.needsCancelBefore;

const sameHints = (a: WorkflowHints, b: WorkflowHints) => {
  const keys = Object.keys(a.nodes);
  return (
    keys.length === Object.keys(b.nodes).length &&
    keys.every((id) => sameNodeHints(a.nodes[id], b.nodes[id]))
  );
};

const emptyHints = (): WorkflowHints => ({ nodes: {} });

const workflowEditorModel: WorkflowEditorModel = {
  workflow: null,
  graph: null,
  versionId: null,
  targetSellerIds: [],
  publishedVersion: null,
  validationIssues: [],
  validationHints: emptyHints(),
  validationStatus: 'idle',
  selectedNodeId: null,
  selectedSnippetId: null,
  sidebarTab: 'step',
  focusedIssue: null,
  reviewingIssues: false,
  mode: { kind: 'idle' },
  quickAdd: null,
  openModal: null,
  targetsModalIntent: null,
  targetsFocusNodeId: null,
  stepPreviewId: null,
  autoLayoutSeq: 0,
  fitViewSeq: 0,
  focusedNodeId: null,
  focusNodeSeq: 0,
  pendingFocusNodeId: null,
  cycleRefusedSeq: 0,
  dirty: false,
  past: [],
  future: [],

  selectedNode: computed((state) =>
    state.graph && state.selectedNodeId
      ? (state.graph.nodes.find((n) => n.id === state.selectedNodeId) ?? null)
      : null,
  ),

  selectedSnippet: computed((state) =>
    state.graph && state.selectedSnippetId
      ? (state.graph.snippets.find((s) => s.id === state.selectedSnippetId) ??
        null)
      : null,
  ),

  load: action((state, payload) => {
    state.workflow = payload.workflow;
    state.graph = payload.graph;
    state.versionId = payload.versionId;
    state.targetSellerIds = payload.targetSellerIds;
    state.publishedVersion = payload.publishedVersion;
    state.validationIssues = [];
    state.validationHints = emptyHints();
    state.validationStatus = 'idle';
    state.selectedNodeId = null;
    state.selectedSnippetId = null;
    state.sidebarTab = 'step';
    state.focusedIssue = null;
    state.reviewingIssues = false;
    state.focusedNodeId = null;
    state.pendingFocusNodeId = null;
    state.mode = { kind: 'idle' };
    state.quickAdd = null;
    state.openModal = null;
    state.targetsModalIntent = null;
    state.targetsFocusNodeId = null;
    state.dirty = false;
    state.past = [];
    state.future = [];
  }),

  undo: action((state) => {
    if (state.past.length === 0 || !state.graph) return;
    const past = [...state.past];
    const previous = past.pop()!;
    const current: HistoryEntry = {
      graph: state.graph,
      targetSellerIds: state.targetSellerIds,
    };
    state.future = [current, ...state.future].slice(0, HISTORY_LIMIT);
    state.past = past;
    applyHistoryEntry(state, previous);
    state.dirty = true;
  }),

  redo: action((state) => {
    if (state.future.length === 0 || !state.graph) return;
    const [next, ...rest] = state.future;
    const current: HistoryEntry = {
      graph: state.graph,
      targetSellerIds: state.targetSellerIds,
    };
    state.past = [...state.past, current].slice(-HISTORY_LIMIT);
    state.future = rest;
    applyHistoryEntry(state, next);
    state.dirty = true;
  }),

  setWorkflow: action((state, workflow) => {
    state.workflow = workflow;
  }),

  setSavedVersion: action((state, version) => {
    state.versionId = version.id;
    state.dirty = false;
  }),

  markPublished: action((state, { version, workflow, targetSellerIds }) => {
    state.workflow = workflow;
    state.publishedVersion = version;
    state.versionId = null;
    if (targetSellerIds) state.targetSellerIds = [...targetSellerIds];
    state.dirty = false;
    // Publicar é ponto de corte: o desfazer não volta para antes da versão
    // publicada (o alvo final entrou fora do histórico). Salvar rascunho não
    // zera (`setSavedVersion`).
    state.past = [];
    state.future = [];
  }),

  setTargetSellerIds: action((state, ids) => {
    if (!state.graph) return;
    const next = Array.from(new Set(ids));
    const same =
      next.length === state.targetSellerIds.length &&
      next.every((id, i) => id === state.targetSellerIds[i]);
    if (same) return;
    pushHistory(state);
    state.targetSellerIds = next;
    setDirty(state);
  }),

  setValidationIssues: action((state, issues) => {
    if (sameIssues(state.validationIssues, issues)) return;
    state.validationIssues = issues;
  }),

  setValidationHints: action((state, hints) => {
    if (sameHints(state.validationHints, hints)) return;
    state.validationHints = hints;
  }),

  setValidationStatus: action((state, status) => {
    if (state.validationStatus !== status) state.validationStatus = status;
  }),

  setName: action((state, name) => {
    if (!state.workflow) return;
    state.workflow.name = name;
  }),

  markPristine: action((state) => {
    state.dirty = false;
  }),

  restoreFromBackup: action((state, { graph, targetSellerIds }) => {
    if (!state.workflow) return;
    state.graph = graph;
    state.targetSellerIds = targetSellerIds;
    state.selectedNodeId = null;
    state.selectedSnippetId = null;
    state.mode = { kind: 'idle' };
    state.past = [];
    state.future = [];
    state.dirty = true;
  }),

  loadFromVersion: action((state, { graph, targetSellerIds }) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = graph;
    state.targetSellerIds = [...targetSellerIds];
    state.selectedNodeId = null;
    state.selectedSnippetId = null;
    state.mode = { kind: 'idle' };
    setDirty(state);
  }),

  selectNode: action((state, id) => {
    state.selectedNodeId = id;
    if (id) {
      state.selectedSnippetId = null;
      state.sidebarTab = 'step';
    }
    state.reviewingIssues = false;
    state.focusedIssue = null;
  }),

  selectSnippet: action((state, id) => {
    state.selectedSnippetId = id;
    if (id) {
      state.selectedNodeId = null;
      state.sidebarTab = 'step';
    }
    state.reviewingIssues = false;
  }),

  setSidebarTab: action((state, tab) => {
    state.sidebarTab = tab;
    // Voltar pra aba do passo sem passo selecionado fecha a lateral.
    if (tab === 'issues') state.selectedSnippetId = null;
  }),

  focusIssue: action((state, { key, nodeId, field }) => {
    const seq = (state.focusedIssue?.seq ?? 0) + 1;
    state.focusedIssue = {
      key,
      nodeId: nodeId ?? null,
      field: field ?? null,
      seq,
    };
    if (!nodeId) return;
    state.selectedNodeId = nodeId;
    state.selectedSnippetId = null;
    state.sidebarTab = 'step';
    state.reviewingIssues = true;
  }),

  setOpenModal: action((state, m) => {
    state.openModal = m;
    // Abrir/fechar pelo caminho normal sempre volta ao modo sem intenção.
    state.targetsModalIntent = null;
    state.targetsFocusNodeId = null;
  }),

  openTargetsForPublish: action((state) => {
    state.openModal = 'targets';
    state.targetsModalIntent = 'publish';
    state.targetsFocusNodeId = null;
  }),

  openTargetsReadOnly: action((state, nodeId) => {
    state.openModal = 'targets';
    state.targetsModalIntent = 'view';
    state.targetsFocusNodeId = nodeId;
  }),

  focusNode: action((state, nodeId) => {
    applyNodeFocus(state, nodeId);
  }),

  requestNodeFocus: action((state, nodeId) => {
    state.pendingFocusNodeId = nodeId;
  }),

  consumeNodeFocus: action((state) => {
    const nodeId = state.pendingFocusNodeId;
    if (!nodeId) return;
    state.pendingFocusNodeId = null;
    applyNodeFocus(state, nodeId);
  }),

  requestFitView: action((state) => {
    state.fitViewSeq += 1;
  }),

  setStepPreview: action((state, nodeId) => {
    state.stepPreviewId = nodeId;
  }),

  requestAutoLayout: action((state) => {
    state.autoLayoutSeq += 1;
  }),

  refuseCycle: action((state) => {
    state.cycleRefusedSeq += 1;
  }),

  addNode: action((state, { kind, position, catalog }) => {
    if (!state.graph) return;
    if (nodesRoomLeft(state.graph.nodes.length, catalog) < 1) return;
    pushHistory(state);
    const node = makeNode(catalog, kind, position ?? { x: 0, y: 0 });
    state.graph = addNodeOp(state.graph, node);
    state.selectedNodeId = node.id;
    setDirty(state);
  }),

  updateNodePatch: action((state, { nodeId, patch }) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = updateNodeOp(state.graph, nodeId, patch);
    setDirty(state);
  }),

  updateNodeConfigPatch: action((state, { nodeId, configPatch, catalog }) => {
    if (!state.graph) return;
    pushHistory(state);
    const before = state.graph.nodes.find((n) => n.id === nodeId);
    let graph: WorkflowGraph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        n.id === nodeId
          ? ({
              ...n,
              config: { ...(n.config as object), ...configPatch } as never,
            } as typeof n)
          : n,
      ),
    };
    const after = graph.nodes.find((n) => n.id === nodeId);
    if (catalog && before && after) {
      graph = dropEdgesOfVanishedPorts(catalog, graph, before, after);
      // Acompanhar entrega de um grupo novo: as regras valem para o grupo.
      graph = syncSnippetFromTrack(catalog, graph, nodeId);
    }
    state.graph = graph;
    clearVanishedSelection(state);
    setDirty(state);
  }),

  setGraphSettings: action((state, settings) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = {
      ...state.graph,
      settings: { ...(state.graph.settings ?? {}), ...settings },
    };
    setDirty(state);
  }),

  removeNode: action((state, nodeId) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = removeNodeOp(state.graph, nodeId);
    if (state.selectedNodeId === nodeId) state.selectedNodeId = null;
    setDirty(state);
  }),

  addNodeFromPort: action(
    (state, { kind, position, fromNodeId, fromPortId, catalog }) => {
      if (!state.graph) return;
      if (nodesRoomLeft(state.graph.nodes.length, catalog) < 1) return;
      pushHistory(state);
      const node = makeNode(catalog, kind, position);
      state.graph = insertNodeFromPort(
        catalog,
        state.graph,
        fromNodeId,
        fromPortId,
        node,
      );
      state.selectedNodeId = node.id;
      state.selectedSnippetId = null;
      state.quickAdd = null;
      setDirty(state);
    },
  ),

  removeSwitchCase: action((state, { nodeId, caseId }) => {
    if (!state.graph) return;
    const node = state.graph.nodes.find((n) => n.id === nodeId);
    if (!node || node.kind !== 'switch') return;
    const cfg = node.config as { cases: Array<{ id: string }> };
    if (!cfg.cases.some((c) => c.id === caseId)) return;
    pushHistory(state);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        n.id === nodeId
          ? ({
              ...n,
              config: {
                ...(n.config as object),
                cases: cfg.cases.filter((c) => c.id !== caseId),
              } as never,
            } as typeof n)
          : n,
      ),
      edges: state.graph.edges.filter(
        (e) => !(e.fromNodeId === nodeId && e.fromPortId === caseId),
      ),
    };
    setDirty(state);
  }),

  applyAutoLayoutPositions: action((state, positions) => {
    if (!state.graph) return;
    // Sem mudança real (clique sem arrasto, drop no mesmo lugar): não gera
    // entrada de undo nem grafo novo — e portanto nenhum re-render.
    const moved = state.graph.nodes.some((n) => {
      const p = positions[n.id];
      return p !== undefined && (p.x !== n.position.x || p.y !== n.position.y);
    });
    if (!moved) return;
    pushHistory(state);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.map((n) =>
        positions[n.id] ? { ...n, position: positions[n.id] } : n,
      ),
    };
  }),

  removeSnippet: action((state, snippetId) => {
    if (!state.graph) return;
    const snippet = state.graph.snippets.find((s) => s.id === snippetId);
    if (!snippet) return;
    pushHistory(state);
    const nodeIds = new Set(snippet.nodeIds);
    state.graph = {
      ...state.graph,
      nodes: state.graph.nodes.filter((n) => !nodeIds.has(n.id)),
      edges: state.graph.edges.filter(
        (e) => !nodeIds.has(e.fromNodeId) && !nodeIds.has(e.toNodeId),
      ),
      snippets: state.graph.snippets.filter((s) => s.id !== snippetId),
    };
    if (state.selectedSnippetId === snippetId) state.selectedSnippetId = null;
    if (state.selectedNodeId && nodeIds.has(state.selectedNodeId)) {
      state.selectedNodeId = null;
    }
    setDirty(state);
  }),

  addEdge: action((state, { fromNodeId, fromPortId, toNodeId, style }) => {
    if (!state.graph) return;
    // Ligação que faria o fluxo voltar: não entra, e a página avisa.
    if (portLinkWouldCycle(state.graph, fromNodeId, fromPortId, toNodeId)) {
      state.cycleRefusedSeq += 1;
      return;
    }
    pushHistory(state);
    state.graph = addEdgeOp(
      state.graph,
      fromNodeId,
      fromPortId,
      toNodeId,
      style,
    );
    setDirty(state);
  }),

  removeEdge: action((state, edgeId) => {
    if (!state.graph) return;
    pushHistory(state);
    state.graph = removeEdgeOp(state.graph, edgeId);
    setDirty(state);
  }),

  startConnect: action((state, { sourceNodeId, sourcePortId, sourceStyle }) => {
    state.mode = {
      kind: 'connecting',
      sourceNodeId,
      sourcePortId,
      sourceStyle,
    };
  }),

  cancelConnect: action((state) => {
    state.mode = { kind: 'idle' };
  }),

  openQuickAdd: action((state, payload) => {
    // Abrir o menu numa porta cancela um "conectar" que estivesse pendente.
    state.mode = { kind: 'idle' };
    state.quickAdd = payload;
  }),

  closeQuickAdd: action((state) => {
    state.quickAdd = null;
  }),

  completeConnect: action((state, { toNodeId }) => {
    if (state.mode.kind !== 'connecting' || !state.graph) return;
    const { sourceNodeId, sourcePortId, sourceStyle } = state.mode;
    if (toNodeId === sourceNodeId) {
      state.mode = { kind: 'idle' };
      return;
    }
    // Destino que fecharia um ciclo: avisa e continua no modo conectar,
    // para a pessoa escolher outro passo (ou sair com Esc).
    if (portLinkWouldCycle(state.graph, sourceNodeId, sourcePortId, toNodeId)) {
      state.cycleRefusedSeq += 1;
      return;
    }
    pushHistory(state);
    state.graph = addEdgeOp(
      state.graph,
      sourceNodeId,
      sourcePortId,
      toNodeId,
      sourceStyle,
    );
    state.mode = { kind: 'idle' };
    setDirty(state);
  }),

  addSnippet: action(
    (state, { kind, attachToNodeId, attachToPortId, position, catalog }) => {
      if (!state.graph) return;
      const origin = position
        ? { x: position.x - 110, y: position.y - 30 }
        : { x: 0, y: 0 };
      const exp =
        kind === 'reenvio'
          ? expandReenvio(catalog, origin)
          : expandSpecSnippet(catalog, kind, origin);
      if (nodesRoomLeft(state.graph.nodes.length, catalog) < exp.nodes.length)
        return;
      pushHistory(state);
      let next: WorkflowGraph = {
        ...state.graph,
        nodes: [...state.graph.nodes, ...exp.nodes],
        edges: [...state.graph.edges, ...exp.edges],
        snippets: [...state.graph.snippets, exp.snippet],
      };
      if (attachToNodeId && attachToPortId) {
        const from = state.graph.nodes.find((n) => n.id === attachToNodeId);
        next = addEdgeOp(
          next,
          attachToNodeId,
          attachToPortId,
          exp.entryNodeId,
          from ? edgeStyleForPort(catalog, from, attachToPortId) : 'default',
        );
      }
      state.graph = next;
      state.quickAdd = null;
      setDirty(state);
    },
  ),

  updateSnippetSpec: action((state, { snippetId, spec, catalog }) => {
    if (!state.graph) return;
    const next = rebuildSnippet(catalog, state.graph, snippetId, spec);
    if (!next) return;
    pushHistory(state);
    state.graph = next;
    clearVanishedSelection(state);
    setDirty(state);
  }),
};

/** Passo selecionado que saiu do grafo (remontagem de grupo) deixa de estar. */
function clearVanishedSelection(state: {
  graph: WorkflowGraph | null;
  selectedNodeId: string | null;
}) {
  if (
    state.selectedNodeId &&
    !state.graph?.nodes.some((n) => n.id === state.selectedNodeId)
  ) {
    state.selectedNodeId = null;
  }
}

/**
 * Seleciona o passo, abre a aba "Passo" e pede ao canvas para centralizar
 * (`focusNodeSeq`). Comum ao `focusNode` e ao `consumeNodeFocus`.
 */
function applyNodeFocus(
  state: {
    selectedNodeId: string | null;
    selectedSnippetId: string | null;
    sidebarTab: SidebarTab;
    focusedIssue: FocusedIssue | null;
    reviewingIssues: boolean;
    focusedNodeId: string | null;
    focusNodeSeq: number;
  },
  nodeId: string,
) {
  state.selectedNodeId = nodeId;
  state.selectedSnippetId = null;
  state.sidebarTab = 'step';
  state.focusedIssue = null;
  state.reviewingIssues = false;
  state.focusedNodeId = nodeId;
  state.focusNodeSeq += 1;
}

export default workflowEditorModel;
